class DrawerCartNote extends HTMLElement {
  constructor() {
    super();

    this.triggers = [
      this.querySelector(".giftnote__drawercart__addnote"),
      this.querySelector(".giftnote__body__close"),
      this.querySelector(".giftnote__overlay"),
    ];
    this.saveBtn = this.querySelector(".giftnote__save");
    this.checkbox = this.querySelector("#gift-message-checkbox");
    this.textarea = this.querySelector("#CartDrawer-note");

    this.debouncedSync = debounce(() => this.syncCart(), ON_CHANGE_DEBOUNCE_TIMER);

    // One listener, one writer. The note and the gift_message attribute are
    // always sent in the same request so they can never disagree.
    this.addEventListener("change", (event) => {
      if (this.checkbox && event.target === this.checkbox) {
        // Untick means "this is not a gift" — drop the message with it.
        if (!this.checkbox.checked && this.textarea) this.textarea.value = "";
        this.updateGiftUI(this.checkbox.checked);
        this.syncCart();
        return;
      }

      this.debouncedSync();
    });

    this.onToggle.bind(this);
  }

  get noteValue() {
    return this.textarea ? this.textarea.value : "";
  }

  // Unticking the box cancels the gift message, so the box and the message can
  // never disagree: a message in the cart always means the box is ticked.
  get isGift() {
    return Boolean(this.checkbox && this.checkbox.checked) &&
      this.noteValue.trim().length > 0;
  }

  syncCart() {
    const body = JSON.stringify({
      note: this.noteValue,
      attributes: { gift_message: this.isGift ? "true" : "false" },
    });

    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body },
    }).catch((e) => {
      console.error(e);
    });
  }

  getDefaultNoteText() {
    const el = document.getElementById("cart-note-default-text");
    return el ? el.dataset.note : "Add a note";
  }

  updateGiftUI(isGift) {
    const label = isGift ? "Gift Message" : this.getDefaultNoteText();

    if (this.triggers[0]) this.triggers[0].textContent = label;

    const noteLabel = this.querySelector("#cart-note-label");
    if (noteLabel) noteLabel.textContent = label;
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, textarea, [tabindex]";
    const focusableElements = () =>
      Array.from(
        this.querySelector(".giftnote__body").querySelectorAll(
          focusableElementsSelector,
        ),
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  connectedCallback() {
    this.init();
  }

  onSave() {
    this.updateGiftUI(this.isGift);
    this.syncCart();
    this.onToggle();
  }

  onToggle() {
    const { focusableElements } = this.getFocusableElements();

    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(focusableElements(), "-1");
      this.triggers[0].focus();
    } else {
      this.setAttribute("open", true);
      setTabindex(focusableElements(), "0");
      this.triggers[1].focus();
    }
  }

  init() {
    this.triggers.forEach((trigger) => {
      trigger?.addEventListener("click", (e) => {
        e.preventDefault();
        this.onToggle();
      });
    });

    this.saveBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.onSave();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.onToggle();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });
  }
}

customElements.define("drawer-cart-note", DrawerCartNote);
